import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, RefreshCw, Search, Building2, CheckCircle2, XCircle } from 'lucide-react';

import { cardAcquirerApi }  from '@/api/cardAcquirer.api';
import { usePermission }    from '@/hooks/usePermission';
import { Permissions }      from '@/constants/permissions';
import { AppRoutes }        from '@/constants/routes';
import { Spinner }          from '@/components/ui/Spinner';

import type { CardAcquirerListDto } from '@/types/cardReconciliation.types';

export const CardAcquirerListPage = () => {
  const navigate          = useNavigate();
  const { hasPermission } = usePermission();
  const canCreate         = hasPermission(Permissions.CARD_ACQUIRER_CREATE);

  const [items,    setItems]   = useState<CardAcquirerListDto[]>([]);
  const [loading,  setLoading] = useState(false);
  const [search,   setSearch]  = useState('');
  const [activeOnly, setActiveOnly] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await cardAcquirerApi.getList(activeOnly);
      const data = res.data?.data;
      if (data) setItems(data);
    } catch (err) {
      console.error('Failed to load card acquirers', err);
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => { load(); }, [load]);

  const filtered = search
    ? items.filter(i =>
        i.acquirerCode.toLowerCase().includes(search.toLowerCase()) ||
        i.acquirerName.toLowerCase().includes(search.toLowerCase()))
    : items;

  return (
    <div className="flex flex-col h-full bg-slate-50 dark:bg-slate-900">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 dark:text-white">Card Acquirers</h1>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
              {filtered.length} acquirer{filtered.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load()} className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-500 dark:text-gray-400" title="Refresh">
              <RefreshCw size={16} />
            </button>
            {canCreate && (
              <button
                onClick={() => navigate(AppRoutes.CARD_ACQUIRER_NEW)}
                className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors"
              >
                <Plus size={15} />
                New Acquirer
              </button>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mt-4">
          <div className="flex-1 max-w-xs relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search acquirer code or name…"
              className="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 dark:border-slate-600 rounded-lg
                         bg-white dark:bg-slate-700 text-slate-900 dark:text-white
                         focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-400 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={activeOnly}
              onChange={e => setActiveOnly(e.target.checked)}
              className="rounded border-slate-300 dark:border-gray-600 text-teal-600 dark:text-teal-400 focus:ring-teal-500"
            />
            Active only
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="flex-1 overflow-auto">
        {loading ? (
          <div className="flex items-center justify-center h-64"><Spinner size="lg" /></div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-slate-400">
            <Building2 size={40} className="mb-3 opacity-40" />
            <p className="text-sm font-medium">No card acquirers found</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-slate-50 dark:bg-slate-800/80 backdrop-blur border-b border-slate-200 dark:border-slate-700">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Code</th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Acquirer Name</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">MDR Rate %</th>
                <th className="px-4 py-3 text-right text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Settlement Days</th>
                <th className="px-4 py-3 text-center text-xs font-semibold text-slate-500 dark:text-gray-400 uppercase tracking-wider">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {filtered.map(acq => (
                <tr
                  key={acq.id}
                  onClick={() => navigate(AppRoutes.CARD_ACQUIRER_DETAIL.replace(':id', acq.id))}
                  className="hover:bg-teal-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors"
                >
                  <td className="px-4 py-3 font-mono font-semibold text-teal-700 dark:text-teal-400">{acq.acquirerCode}</td>
                  <td className="px-4 py-3 font-medium text-slate-900 dark:text-white">{acq.acquirerName}</td>
                  <td className="px-4 py-3 text-right font-mono text-slate-700 dark:text-slate-300">{(acq.mdrRate * 100).toFixed(3)}</td>
                  <td className="px-4 py-3 text-right text-slate-600 dark:text-slate-400">{acq.settlementDelayDays}d</td>
                  <td className="px-4 py-3 text-center">
                    {acq.isActive ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                        <CheckCircle2 size={11} /> Active
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">
                        <XCircle size={11} /> Inactive
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
